import { Vec2, raySegmentIntersection } from '../utils/math';
import { Ray, TracedSegment, PX_TO_METERS } from '../engine/Ray';
import { Component, LaserSource, Detector, SurfaceHit } from '../engine/Component';
import { Scene } from '../engine/Scene';
import { CHANNEL_COUNT, channelToWavelength } from '../utils/color';

const MAX_BOUNCES = 64;
const MAX_RAY_LENGTH = 5000;
const MIN_INTENSITY = 0.01;
const WHITE_LIGHT_CHANNELS = 16;

export class RayTracer {
  trace(scene: Scene): TracedSegment[] {
    const segments: TracedSegment[] = [];
    const components = scene.getAll();

    for (const comp of components) {
      if (comp instanceof Detector) comp.resetBins();
    }

    const lasers = components.filter((c): c is LaserSource => c.type === 'laser');
    for (const laser of lasers) {
      this.emitLaser(laser, components, segments);
    }

    return segments;
  }

  private emitLaser(laser: LaserSource, components: Component[], segments: TracedSegment[]): void {
    const positions = this.getBeamPositions(laser);
    const dir = laser.getEmitDirection();

    for (const origin of positions) {
      if (laser.whiteLight) {
        const step = Math.floor(CHANNEL_COUNT / WHITE_LIGHT_CHANNELS);
        for (let i = 0; i < CHANNEL_COUNT; i += step) {
          const wl = channelToWavelength(i);
          const ray = new Ray(
            origin, dir, wl, laser.power / WHITE_LIGHT_CHANNELS, laser.id,
            undefined, 0, 0, true, laser.coherenceLength, true,
          );
          this.traceRay(ray, components, segments, 0);
        }
      } else {
        const ray = new Ray(
          origin, dir, laser.wavelength, laser.power, laser.id,
          undefined, 0, 0, true, laser.coherenceLength, false,
        );
        this.traceRay(ray, components, segments, 0);
      }
    }
  }

  private getBeamPositions(laser: LaserSource): Vec2[] {
    const origin = laser.getEmitOrigin();
    if (laser.beamWidth <= 0 || laser.rayCount <= 1) {
      return [origin];
    }

    const n = laser.rayCount;
    const halfW = laser.beamWidth / 2;
    const perp = laser.getEmitDirection().perpendicular();
    const positions: Vec2[] = [];

    for (let i = 0; i < n; i++) {
      const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
      positions.push(origin.add(perp.scale(t * halfW)));
    }

    return positions;
  }

  private traceRay(ray: Ray, components: Component[], segments: TracedSegment[], depth: number): void {
    if (depth >= MAX_BOUNCES || ray.intensity < MIN_INTENSITY) return;

    const hit = this.findNearestHit(ray, components);

    if (hit) {
      // Accumulate phase and path length for this segment
      const segLen = ray.position.distanceTo(hit.point);
      const wavelengthPx = ray.wavelength / PX_TO_METERS;
      ray.phase += 2 * Math.PI * segLen / wavelengthPx;
      ray.pathLength += segLen;

      // Check coherence
      if (ray.coherenceLength > 0 && ray.pathLength > ray.coherenceLength) {
        ray.coherent = false;
      }

      segments.push({
        start: ray.position,
        end: hit.point,
        wavelength: ray.wavelength,
        intensity: ray.intensity,
        sourceId: ray.sourceId,
        phase: ray.phase,
        pathLength: ray.pathLength,
        coherent: ray.coherent,
      });

      const results = hit.component.handleRayHit(ray, hit);
      for (const result of results) {
        this.traceRay(result.ray, components, segments, depth + 1);
      }
    } else {
      const segLen = MAX_RAY_LENGTH;
      const wavelengthPx = ray.wavelength / PX_TO_METERS;
      ray.phase += 2 * Math.PI * segLen / wavelengthPx;
      ray.pathLength += segLen;
      if (ray.coherenceLength > 0 && ray.pathLength > ray.coherenceLength) {
        ray.coherent = false;
      }

      const end = ray.position.add(ray.direction.scale(MAX_RAY_LENGTH));
      segments.push({
        start: ray.position,
        end,
        wavelength: ray.wavelength,
        intensity: ray.intensity,
        sourceId: ray.sourceId,
        phase: ray.phase,
        pathLength: ray.pathLength,
        coherent: ray.coherent,
      });
    }
  }

  private findNearestHit(ray: Ray, components: Component[]): SurfaceHit | null {
    let nearest: SurfaceHit | null = null;
    let nearestT = Infinity;

    for (const comp of components) {
      if (!comp.isOpticalSurface()) continue;

      const segments = comp.getSegments();
      const normals = comp.getNormals();

      for (let i = 0; i < segments.length; i++) {
        const result = raySegmentIntersection(
          { origin: ray.position, direction: ray.direction },
          segments[i],
        );

        if (result && result.t < nearestT) {
          nearestT = result.t;
          const normal = normals[i];
          const entering = ray.direction.dot(normal) < 0;
          nearest = {
            t: result.t,
            point: result.point,
            normal: entering ? normal : normal.negate(),
            component: comp,
            segmentIndex: i,
            entering,
          };
        }
      }
    }

    return nearest;
  }
}
